// ============================================
// Ops AI Agent Chat — Phase 7 Section 11
// Natural language query interface embedded in
// the Platform Health Overview tab.
// ============================================

import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { opsAgentQueryAction } from '../../../services/firestore/opsService';

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  pass: '#059669',
  warn: '#B45309',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
  bgElevated: '#F4F4F5',
};

const SUGGESTED_QUESTIONS = [
  'Is the platform healthy?',
  'What needs attention?',
  'How much did we spend this week?',
  'Are we on budget?',
  'Are we honoring our TOS commitments?',
  'Which compliance checks are failing?',
];

interface ChatMessage {
  role: 'user' | 'agent';
  text: string;
  status?: string;
  score?: number;
  actionItems?: { priority: string; description: string; suggestedAction: string }[];
  timestamp: string;
}

export function OpsAgentChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const askQuestion = async (question: string) => {
    if (!question.trim() || loading) return;

    const userMsg: ChatMessage = { role: 'user', text: question.trim(), timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const result = await opsAgentQueryAction(question.trim());
      const agentMsg: ChatMessage = {
        role: 'agent',
        text: result.answer,
        status: result.status,
        score: result.score,
        actionItems: result.actionItems,
        timestamp: result.queriedAt,
      };
      setMessages((prev) => [...prev, agentMsg]);
    } catch (err: any) {
      setError(err.message || 'Query failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    askQuestion(input);
  };

  const statusColor = (s?: string) =>
    s === 'healthy' ? C.pass : s === 'degraded' ? C.warn : s === 'critical' ? C.rose : C.textMuted;

  const priorityColor = (p: string) =>
    p === 'P0' ? C.rose : p === 'P1' ? C.warn : C.textMuted;

  return (
    <div style={{
      background: C.bgCard,
      border: `1px solid ${C.border}`,
      borderRadius: 4,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '14px 20px',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <MessageSquare size={16} style={{ color: C.rose }} />
        <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted }}>
          Ops Agent
        </span>
        <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 'auto' }}>
          Ask about platform health, costs, compliance
        </span>
      </div>

      {/* Messages area */}
      <div style={{
        padding: 20,
        minHeight: 100,
        maxHeight: 400,
        overflowY: 'auto',
      }}>
        {messages.length === 0 && !loading && (
          <div>
            <p style={{ fontSize: 14, color: C.textSec, marginBottom: 12 }}>
              Ask me anything about your platform. Try:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => askQuestion(q)}
                  style={{
                    padding: '6px 12px',
                    fontSize: 13,
                    fontFamily: "'DM Sans', sans-serif",
                    color: C.rose,
                    background: '#FFF1F2',
                    border: `1px solid #FECDD3`,
                    borderRadius: 16,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FECDD3')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#FFF1F2')}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            {msg.role === 'user' ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  background: C.rose,
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: '16px 16px 4px 16px',
                  maxWidth: '80%',
                  fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {msg.text}
                </div>
              </div>
            ) : (
              <div>
                {/* Status badge */}
                {msg.status && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: statusColor(msg.status),
                      textTransform: 'uppercase',
                    }}>
                      {msg.status}
                    </span>
                    {msg.score != null && (
                      <span style={{ fontSize: 12, color: C.textMuted }}>
                        Score: {msg.score}
                      </span>
                    )}
                  </div>
                )}

                {/* Answer */}
                <div style={{
                  background: C.bgElevated,
                  padding: '14px 18px',
                  borderRadius: '4px 16px 16px 16px',
                  maxWidth: '90%',
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: C.text,
                  fontFamily: "'DM Sans', sans-serif",
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.text}
                </div>

                {/* Action items */}
                {msg.actionItems && msg.actionItems.length > 0 && (
                  <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>
                      Action Items
                    </p>
                    {msg.actionItems.map((item, j) => (
                      <div key={j} style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                        padding: '6px 0',
                        borderBottom: j < msg.actionItems!.length - 1 ? `1px solid ${C.border}` : 'none',
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: priorityColor(item.priority),
                          flexShrink: 0,
                          marginTop: 2,
                        }}>
                          {item.priority}
                        </span>
                        <div>
                          <p style={{ fontSize: 13, color: C.text }}>{item.description}</p>
                          <p style={{ fontSize: 12, color: C.textMuted }}>{item.suggestedAction}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.textMuted, fontSize: 13 }}>
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            Analyzing platform data...
          </div>
        )}

        {error && (
          <p style={{ fontSize: 13, color: C.rose, marginTop: 8 }}>{error}</p>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={{
        display: 'flex',
        gap: 8,
        padding: '12px 20px',
        borderTop: `1px solid ${C.border}`,
        background: C.bgElevated,
      }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about health, costs, compliance..."
          disabled={loading}
          style={{
            flex: 1,
            padding: '10px 14px',
            fontSize: 14,
            fontFamily: "'DM Sans', sans-serif",
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            outline: 'none',
            color: C.text,
            background: '#fff',
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = C.rose)}
          onBlur={(e) => (e.currentTarget.style.borderColor = C.border)}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          style={{
            padding: '10px 16px',
            fontSize: 14,
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            background: loading || !input.trim() ? C.border : C.rose,
            color: loading || !input.trim() ? C.textMuted : '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background 0.15s',
          }}
        >
          <Send size={14} />
          Ask
        </button>
      </form>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
